/**
 * Football field coordinate system utility
 *
 * Handles conversion between two coordinate systems:
 * 1. FEET COORDINATES (Storage/Logic)
 *    - Origin: Bottom-left (0, 0)
 *    - Y-axis: Increases upward (natural)
 *    - Width: 160 feet
 *
 * 2. WEB PIXEL COORDINATES (Rendering)
 *    - Origin: Top-left (0, 0)
 *    - Y-axis: Increases downward (CSS/Canvas default)
 *    - Width: Dynamic (responsive)
 */

import { FIELD_WIDTH_FEET } from '@/constants'
import type { Coordinate, FeetCoordinate, PixelCoordinate } from '@/types'

export class FieldCoordinateSystem {
	private containerWidth: number
	private containerHeight: number

	constructor(containerWidth: number, containerHeight: number) {
		this.containerWidth = containerWidth
		this.containerHeight = containerHeight
	}

	/**
	 * Get the current scale factor (pixels per foot)
	 */
	get scale(): number {
		return this.containerWidth / FIELD_WIDTH_FEET
	}

	/**
	 * Convert feet coordinates to web pixel coordinates
	 * Handles Y-axis flip: feet Y=0 is at bottom, pixel Y=0 is at top
	 */
	feetToPixels(feetX: number, feetY: number): PixelCoordinate {
		const pixelX = feetX * this.scale
		const pixelY = this.containerHeight - feetY * this.scale
		return { x: pixelX, y: pixelY }
	}

	/**
	 * Transform a coordinate from feet to pixels
	 * Convenience method that accepts a Coordinate object
	 */
	transformPoint(point: Coordinate): PixelCoordinate {
		return this.feetToPixels(point.x, point.y)
	}

	/**
	 * Convert web pixel coordinates to feet coordinates
	 * Handles Y-axis flip: pixel Y=0 is at top, feet Y=0 is at bottom
	 */
	pixelsToFeet(pixelX: number, pixelY: number): FeetCoordinate {
		const feetX = pixelX / this.scale
		const feetY = (this.containerHeight - pixelY) / this.scale
		return { x: feetX, y: feetY }
	}

	/**
	 * Update container dimensions (e.g., when window resizes)
	 */
	updateDimensions(width: number, height: number): void {
		this.containerWidth = width
		this.containerHeight = height
	}

	/**
	 * Get current container dimensions
	 */
	getDimensions(): { width: number; height: number } {
		return {
			width: this.containerWidth,
			height: this.containerHeight,
		}
	}

	/**
	 * Calculate the height in feet based on current container height
	 */
	getHeightInFeet(): number {
		return this.containerHeight / this.scale
	}

	/**
	 * Convert screen pixel coordinates to feet, accounting for zoom/pan viewport transform
	 * @param screenX - X coordinate relative to container
	 * @param screenY - Y coordinate relative to container
	 * @param zoom - Current zoom level (1.0 = baseline)
	 * @param panX - Pan offset in pixels
	 * @param panY - Pan offset in pixels
	 */
	screenToFeet(
		screenX: number,
		screenY: number,
		zoom: number,
		panX: number,
		panY: number,
	): FeetCoordinate {
		// Remove transform to get canvas pixels
		const canvasX = (screenX - panX) / zoom
		const canvasY = (screenY - panY) / zoom

		// Then convert to feet
		return this.pixelsToFeet(canvasX, canvasY)
	}

	/**
	 * Convert feet to screen pixels, accounting for zoom/pan viewport transform
	 * @param feetX - X coordinate in feet
	 * @param feetY - Y coordinate in feet
	 * @param zoom - Current zoom level (1.0 = baseline)
	 * @param panX - Pan offset in pixels
	 * @param panY - Pan offset in pixels
	 */
	feetToScreen(
		feetX: number,
		feetY: number,
		zoom: number,
		panX: number,
		panY: number,
	): PixelCoordinate {
		// Convert to canvas pixels
		const canvasPixels = this.feetToPixels(feetX, feetY)

		// Apply transform
		return {
			x: canvasPixels.x * zoom + panX,
			y: canvasPixels.y * zoom + panY,
		}
	}

	/**
	 * Compute drag offset in feet-space for smooth zoomed dragging
	 * Call on mousedown to get the offset between cursor and element center
	 *
	 * This ensures drag operations work correctly at any zoom level by calculating
	 * the offset in feet-space rather than screen pixels, which don't scale with zoom.
	 *
	 * @param cursorScreenX - Cursor X position relative to container
	 * @param cursorScreenY - Cursor Y position relative to container
	 * @param elementCenterFeetX - Element center X in feet coordinates
	 * @param elementCenterFeetY - Element center Y in feet coordinates
	 * @param zoom - Current zoom level (1.0 = baseline)
	 * @param panX - Pan offset in pixels
	 * @param panY - Pan offset in pixels
	 * @returns Offset in feet coordinates (elementCenter - cursor)
	 */
	computeDragOffsetFeet(
		cursorScreenX: number,
		cursorScreenY: number,
		elementCenterFeetX: number,
		elementCenterFeetY: number,
		zoom: number,
		panX: number,
		panY: number,
	): FeetCoordinate {
		const cursorFeet = this.screenToFeet(cursorScreenX, cursorScreenY, zoom, panX, panY)
		return {
			x: elementCenterFeetX - cursorFeet.x,
			y: elementCenterFeetY - cursorFeet.y,
		}
	}
}

/**
 * Y-flip for viewBox rendering: field bottom-left origin → SVG top-left origin.
 * Used by all read-only renderers (thumbnails, exports, reference visuals).
 */
export function viewBoxFlipY(
	point: { x: number; y: number },
	height: number,
): { x: number; y: number } {
	return { x: point.x, y: height - point.y }
}

/**
 * Helper function to create a coordinate system instance
 */
export function createCoordinateSystem(
	containerWidth: number,
	containerHeight: number,
): FieldCoordinateSystem {
	return new FieldCoordinateSystem(containerWidth, containerHeight)
}

